import React from 'react'
import { MapContainer, TileLayer, GeoJSON, useMap } from 'react-leaflet'
import { Feature, FeatureCollection } from 'geojson'
import worldGeoJSON from '../../constants/countries.geo.json'
import { cn } from "../../lib/utils"
import L from 'leaflet'
import 'leaflet/dist/leaflet.css'

interface DataPoint {
  country: string
  value: number
  [key: string]: any
}

interface CustomGeoMapProps {
  data: DataPoint[]
  className?: string
  height?: number
  showTooltip?: boolean
  colorScale?: string[]
}

// Component to handle map zoom to fit all features
const MapBounds: React.FC<{ dataMap: Map<string, DataPoint>, onMapReady?: () => void }> = ({ dataMap, onMapReady }) => {
  const map = useMap()

  React.useEffect(() => {
    // Series of invalidations to ensure proper rendering
    const timeouts: NodeJS.Timeout[] = []

    // Initial invalidation
    timeouts.push(setTimeout(() => {
      map.invalidateSize()
    }, 100))

    // Second invalidation for modal animation completion
    timeouts.push(setTimeout(() => {
      map.invalidateSize()
    }, 300))

    // Final invalidation
    timeouts.push(setTimeout(() => {
      map.invalidateSize()
      onMapReady?.()
    }, 500))

    if (!worldGeoJSON) return

    const featuresWithData = (worldGeoJSON as FeatureCollection).features.filter(f => {
      const countryName = f.properties?.ADMIN || f.properties?.name
      return dataMap.has(countryName)
    })

    if (featuresWithData.length > 0) {
      const latlngs: L.LatLngExpression[] = []

      featuresWithData.forEach(f => {
        const layer = L.geoJSON(f as any)
        const bounds = layer.getBounds()
        latlngs.push(bounds.getCenter())
      })

      const groupBounds = L.latLngBounds(latlngs)

      timeouts.push(
        setTimeout(() => {
          map.fitBounds(groupBounds, {
            padding: [50, 50],
            maxZoom: 5,
          })
          map.invalidateSize()
        }, 600)
      )
    }
    return () => {
      timeouts.forEach(t => clearTimeout(t))
    }
  }, [map, dataMap, onMapReady])

  return null
}


// Wrapper component for modal rendering
export const ModalGeoMap: React.FC<CustomGeoMapProps & { isOpen: boolean }> = ({
  isOpen,
  ...props
}) => {
  const [mapKey, setMapKey] = React.useState(0)

  React.useEffect(() => {
    if (isOpen) {
      // Force re-render when modal opens
      setTimeout(() => {
        setMapKey(prev => prev + 1)
      }, 50)
    }
  }, [isOpen])

  if (!isOpen) return null

  return <CustomGeoMap {...props} key={mapKey} />
}

export const CustomGeoMap: React.FC<CustomGeoMapProps> = ({
  data,
  className,
  height = 500,
  showTooltip = true,
  colorScale,   // ✅ add this here
}) => {
  const [hoveredCountry, setHoveredCountry] = React.useState<string | null>(null)
  const [isMapReady, setIsMapReady] = React.useState(false)

  // Map input data by country name
  const dataMap = React.useMemo(() => {
    const map = new Map<string, DataPoint>()
    if (!Array.isArray(data)) return map
    data.forEach(item => {
      const normalized = item.country
        .replace('United States', 'United States of America')
        .replace('UK', 'United Kingdom')
        .replace('South Korea', 'Korea, Republic of')
        .replace('North Korea', "Korea, Democratic People's Republic of")
      map.set(normalized, item)
      map.set(item.country, item)
    })
    return map
  }, [data])

  const maxValue = Array.isArray(data) && data.length > 0 ? Math.max(...data.map(d => d.value)) : 0
  const minValue = Array.isArray(data) && data.length > 0 ? Math.min(...data.map(d => d.value)) : 0

  const getColor = (countryName: string) => {
    const countryData = dataMap.get(countryName)
    if (!countryData) return '#e5e7eb'

    const value = countryData.value
    const ratio = (value - minValue) / (maxValue - minValue || 1)

    if (colorScale && colorScale.length > 1) {
      const idx = Math.floor(ratio * (colorScale.length - 1))
      return colorScale[idx]
    }

    // fallback to fixed greens
    if (ratio > 0.8) return '#059669'
    if (ratio > 0.6) return '#10b981'
    if (ratio > 0.4) return '#34d399'
    if (ratio > 0.2) return '#86efac'
    return '#bbf7d0'
  }


  const formatValue = (value: number) => {
    if (value >= 1_000_000) return `${(value / 1_000_000).toFixed(1)}M`
    if (value >= 1_000) return `${(value / 1_000).toFixed(1)}K`
    return value.toFixed(0)
  }

  const onEachFeature = (feature: Feature, layer: any) => {
    const countryName = feature.properties?.ADMIN || feature.properties?.name
    const countryData = dataMap.get(countryName)

    if (countryData && showTooltip) {
      layer.bindPopup(`
        <div style="padding: 8px;">
          <div style="font-weight: 600; font-size: 14px; margin-bottom: 4px;">
            ${countryData.country}
          </div>
          <div style="font-size: 18px; font-weight: 700; color: #059669;">
            ${formatValue(countryData.value)}
          </div>
        </div>
      `)
    }

    layer.on({
      mouseover: (e: any) => {
        if (countryData) {
          setHoveredCountry(countryData.country)
          e.target.setStyle({
            weight: 3,
            color: '#fff',
            fillOpacity: 0.9
          })
        }
      },
      mouseout: (e: any) => {
        setHoveredCountry(null)
        e.target.setStyle({
          weight: 1,
          color: '#fff',
          fillOpacity: 0.7
        })
      }
    })
  }

  const geoJsonStyle = (feature: Feature | undefined) => {
    if (!feature) return {}
    const countryName = feature.properties?.ADMIN || feature.properties?.name
    const isHovered = hoveredCountry === countryName

    return {
      fillColor: getColor(countryName),
      weight: isHovered ? 3 : 1,
      opacity: 1,
      color: '#fff',
      fillOpacity: isHovered ? 0.9 : 0.7
    }
  }

  return (
    <div className={cn("w-full relative", className)} style={{ zIndex: 0 }}>
      <MapContainer
        center={[20, 0]}
        zoom={2}
        style={{ height: `${height}px`, width: '100%', position: 'relative', zIndex: 0 }}
        className="rounded-lg"
        scrollWheelZoom={false}
        attributionControl={false}
      >
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />

        <MapBounds dataMap={dataMap} onMapReady={() => setIsMapReady(true)} />

        <GeoJSON
          data={worldGeoJSON as FeatureCollection}
          style={geoJsonStyle}
          onEachFeature={onEachFeature}
        />
      </MapContainer>
    </div>
  )
}
